import {
    MenuItem,
    ListItemText,
    ListItemIcon,
    Box,
    useTheme,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { Icon } from 'packages/eid-icons'
import { useRef, useState } from 'react'
import useSubcomponents from 'useSubcomponents'
import { defaultDataProtectedSubcomponent } from 'utils'

const StyledMenuItem = styled(MenuItem)(({ theme, isSubTabItem }) => ({
    fontSize: '1.6rem',
    position: 'relative',
    minHeight: '4rem',
    paddingLeft: '3rem',
    backgroundColor: isSubTabItem ? 'rgba(48, 127, 193, 0.07)' : 'unset',
    color: isSubTabItem ? theme.palette.primary.main : 'black',
    borderLeft: isSubTabItem
        ? `2px solid ${theme.palette.primary.main}`
        : 'none',
    cursor: 'pointer',
    '&:focus': {
        backgroundColor: 'rgba(48, 127, 193, 0.07)',
        outline: 'none',
    },
    '&:focus-visible': {
        backgroundColor: 'rgba(48, 127, 193, 0.07)',
        outline: `2px solid ${theme.palette.primary.main}`,
        outlineOffset: '2px',
    },
    '&.Mui-selected': {
        backgroundColor: isSubTabItem
            ? `${theme.palette.primary.main} !important`
            : 'rgba(48, 127, 193, 0.07) !important',
        borderLeft: `2px solid ${theme.palette.primary.main}`,
        color: isSubTabItem ? 'white' : 'black',
        '& img': {
            opacity: 1,
            filter: 'grayscale(0%)',
        },
    },
}))

const StyledListItemIcon = styled(ListItemIcon)({
    minWidth: '4rem !important',
    color: 'black',
})

const TabsItem = ({
    label,
    icon,
    selected,
    onClick,
    subTabs,
    isSubTabItem,
    onSubTabChange,
    subActiveTab,
    protectedSubcomonent,
    isSubTabVisible = true,
}) => {
    const [expandSubTabs, setExpandSubTabs] = useState(true)
    const theme = useTheme()
    const { hasAccessToControl } = useSubcomponents()
    const keyboardToggledRef = useRef(false)
    
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            event.stopPropagation()
            keyboardToggledRef.current = true
            if (selected) {
                setExpandSubTabs((pre) => !pre)
            } else {
                setExpandSubTabs(true)
                onClick()
            }
        }
    }
    
    return (
        <>
            <StyledMenuItem
                onClick={() => {
                    if (keyboardToggledRef.current) {
                        keyboardToggledRef.current = false
                        return
                    }
                    if (selected) {
                        setExpandSubTabs((pre) => !pre)
                    } else {
                        setExpandSubTabs(true)
                        onClick()
                    }
                }}
                onKeyDown={handleKeyDown}
                tabIndex={isSubTabItem ? (isSubTabVisible ? 0 : -1) : 0}
                role={isSubTabItem ? 'menuitem' : 'tab'}
                aria-selected={selected}
                selected={selected}
                isSubTabItem={isSubTabItem}
                data-protectedsubcomponent={
                    protectedSubcomonent || defaultDataProtectedSubcomponent
                }
            >
                {icon && <StyledListItemIcon>{icon}</StyledListItemIcon>}
                <ListItemText>
                    <Box
                        sx={{
                            marginLeft: isSubTabItem ? '4rem' : '0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignContent: 'center',
                            whiteSpace:
                                subTabs && subTabs.length > 0
                                    ? 'normal'
                                    : 'nowrap',
                        }}
                    >
                        <Box sx={{ whiteSpace: 'break-spaces' }}>{label}</Box>
                        {subTabs && subTabs.length > 0 && (
                            <Box>
                                <Icon
                                    style={{
                                        height: '1.6rem',
                                        width: '1.6rem',
                                    }}
                                    name={'Arrow'}
                                    direction={
                                        selected && expandSubTabs
                                            ? 'up'
                                            : 'down'
                                    }
                                    color={theme.palette.primary.main}
                                />
                            </Box>
                        )}
                    </Box>
                </ListItemText>
            </StyledMenuItem>
            <Box
                sx={{
                    transform: `scaleY(${
                        selected && subTabs && expandSubTabs ? 1 : 0
                    })`,
                    transition: 'transform 0.2s ease-out-in',
                    transformOrigin:
                        selected && subTabs && expandSubTabs ? 'top' : 'bottom',
                    maxHeight:
                        selected && subTabs && expandSubTabs
                            ? 'fit-content'
                            : 0,
                }}
                aria-hidden={!(selected && subTabs && expandSubTabs)}
            >
                {subTabs
                    ?.filter(
                        (t) =>
                            t.display &&
                            (!t?.requireAccess?.control ||
                                hasAccessToControl(t?.requireAccess?.control)),
                    )
                    .map((subTab, index) => (
                        <TabsItem
                            key={subTab.label + index}
                            label={subTab.label}
                            selected={subActiveTab === subTab.value}
                            onClick={() => onSubTabChange(subTab.value)}
                            isSubTabItem
                            isSubTabVisible={selected && expandSubTabs}
                            protectedSubcomonent={
                                subTab?.requireAccess?.control
                            }
                        />
                    ))}
            </Box>
        </>
    )
}

const LeftPaneTabs = ({
    active,
    onChange,
    tabs,
    onSubTabChange = (subTab) => {},
    subActiveTab = null,
}) => {
    const { controls } = useSubcomponents()

    const isAccessible = (tab) => {
        return controls.includes(tab.requireAccess?.control)
    }

    return (
        <>
            {tabs &&
                tabs
                    .filter((x) => x.display === true)
                    .filter((y) => {
                        if (y.requireAccess) {
                            return isAccessible(y)
                        } else {
                            return true
                        }
                    })
                    .map((tab, index) => (
                        <TabsItem
                            key={`${tab.value}_${index}`}
                            label={tab.label}
                            onClick={() => {
                                onChange(tab.value)
                            }}
                            selected={active === tab.value}
                            icon={tab.icon}
                            subTabs={tab.subTabs}
                            onSubTabChange={onSubTabChange}
                            subActiveTab={subActiveTab}
                            protectedSubcomonent={tab?.requireAccess?.control}
                        />
                    ))}
        </>
    )
}

export default LeftPaneTabs
